// Gasto por org — cuánto le cuesta a la plataforma cada organización.
//
// Solo el dueño (Samuel): el servidor responde 403 a cualquier otro. Fuentes:
// IA (texto y voz en tiempo real), WhatsApp (Meta), Twilio y ElevenLabs. Lo
// estimado con tarifa se marca; la factura real de Meta va aparte para conciliar.
//
// Gráfica: barras apiladas por día, 4 categorías en orden FIJO (IA, WhatsApp,
// Twilio, ElevenLabs) con la paleta validada (dataviz, claro y oscuro). Dos tonos
// quedan < 3:1 en claro → alivio: tabla por org con cifras + tooltip con valores.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, CircleDollarSign, Lock, MessageCircle, Phone, Receipt, RefreshCw, Sparkles, Mic } from 'lucide-react';

import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { OsApp, OsTabs, osActionBtn } from '@/modules/orion-shell/OsApp';
import { getResumenGasto, type FacturaMeta, type GastoOrg, type Proveedor, type ResumenGasto } from './api';

const PROVEEDORES: { key: Proveedor; label: string; icon: JSX.Element; tone: string }[] = [
  { key: 'openai', label: 'IA', icon: <Sparkles size={18} />, tone: 'violet' },
  { key: 'meta_whatsapp', label: 'WhatsApp', icon: <MessageCircle size={18} />, tone: 'indigo' },
  { key: 'twilio', label: 'Twilio', icon: <Phone size={18} />, tone: 'blue' },
  { key: 'elevenlabs', label: 'ElevenLabs', icon: <Mic size={18} />, tone: 'magenta' },
];
const COLOR = (i: number) => `var(--gasto-${i + 1})`;

const ESTILOS = `
.viz-gasto { --gasto-1:#2a78d6; --gasto-2:#eb6834; --gasto-3:#1baf7a; --gasto-4:#eda100; }
@media (prefers-color-scheme: dark) {
  :root:where(:not([data-theme="light"])) .viz-gasto { --gasto-1:#3987e5; --gasto-2:#d95926; --gasto-3:#199e70; --gasto-4:#c98500; }
}
:root[data-theme="dark"] .viz-gasto { --gasto-1:#3987e5; --gasto-2:#d95926; --gasto-3:#199e70; --gasto-4:#c98500; }
`;

type Periodo = 'mes' | 'mes_pasado' | '7d' | '30d';
const PERIODOS: { key: Periodo; label: string }[] = [
  { key: 'mes', label: 'Este mes' },
  { key: 'mes_pasado', label: 'Mes pasado' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
];

function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rango(p: Periodo): [string, string] {
  const hoy = new Date();
  if (p === 'mes') return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), iso(hoy)];
  if (p === 'mes_pasado') {
    return [iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)), iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0))];
  }
  const dias = p === '7d' ? 6 : 29;
  return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - dias)), iso(hoy)];
}

function useDinero(tc: number | null, enMxn: boolean) {
  return (usd: number | null | undefined) => {
    if (usd === null || usd === undefined) return '—';
    const v = enMxn && tc ? usd * tc : usd;
    if (v > 0 && v < 0.01) return enMxn && tc ? '< $0.01' : '< US$0.01';
    const n = v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return enMxn && tc ? `$${n}` : `US$${n}`;
  };
}

export default function CostosPage() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [datos, setDatos] = useState<ResumenGasto | null>(null);
  const [estado, setEstado] = useState<'cargando' | 'ok' | 'error' | 'prohibido'>('cargando');
  const [error, setError] = useState('');
  const [enMxn, setEnMxn] = useState(true);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    let vivo = true;
    const [d, h] = rango(periodo);
    setEstado('cargando');
    getResumenGasto(d, h)
      .then((r) => { if (vivo) { setDatos(r); setEstado('ok'); } })
      .catch((e: any) => {
        if (!vivo) return;
        if (e?.status === 403) setEstado('prohibido');
        else { setError(e?.message || 'No se pudo cargar el gasto'); setEstado('error'); }
      });
    return () => { vivo = false; };
  }, [periodo, recarga]);

  const tc = datos?.tipo_de_cambio_mxn ?? null;
  const dinero = useDinero(tc, enMxn);

  const subtitulo = estado === 'ok' && datos
    ? `Total ${dinero(datos.total_usd)} · ${datos.orgs.length} ${datos.orgs.length === 1 ? 'organización' : 'organizaciones'} · solo tú lo ves`
    : 'Cuánto te cuesta cada organización · solo tú lo ves';

  return (
    <div className="viz-gasto w-full">
      <style>{ESTILOS}</style>
      <OsApp
        icon={CircleDollarSign}
        title="Gasto por org"
        subtitle={subtitulo}
        tabs={estado !== 'prohibido' && (
          <OsTabs label="Periodo" value={periodo} onChange={setPeriodo} items={PERIODOS} />
        )}
        actions={estado !== 'prohibido' && (
          <>
            {tc && (
              <button type="button" onClick={() => setEnMxn(!enMxn)} className={osActionBtn}
                aria-label={enMxn ? 'Ver en dólares' : 'Ver en pesos'}>
                {enMxn ? 'MXN' : 'USD'}
              </button>
            )}
            <button type="button" onClick={() => setRecarga((n) => n + 1)} disabled={estado === 'cargando'}
              className={osActionBtn} aria-label="Actualizar" title="Actualizar">
              <RefreshCw className={`h-4 w-4 ${estado === 'cargando' ? 'animate-spin' : ''}`} aria-hidden />
            </button>
          </>
        )}
      >
        {estado === 'prohibido' && (
          <EmptyState icon={<Lock size={18} />} title="Solo el dueño de la plataforma ve el gasto por org">
            Entra con tu usuario de Brainstream para ver cuánto cuesta cada organización.
          </EmptyState>
        )}
        {estado === 'cargando' && <Loading label="Calculando el gasto…" />}
        {estado === 'error' && <ErrorNote>{error}</ErrorNote>}
        {estado === 'ok' && datos && (
          <div className="flex flex-col gap-3 sm:gap-4">
            <Totales datos={datos} dinero={dinero} />
            <Card title="Gasto por día" titleId="gasto-dia">
              <GraficaDiaria datos={datos} dinero={dinero} />
            </Card>
            <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-12">
              <Card title="Por organización" titleId="gasto-orgs" className="lg:col-span-8">
                <ListaOrgs orgs={datos.orgs} total={datos.total_usd} dinero={dinero} />
              </Card>
              <div className="flex flex-col gap-3 sm:gap-4 lg:col-span-4">
                {datos.facturas_meta && (
                  <Card title="Factura real de WhatsApp" titleId="gasto-meta">
                    <FacturasMeta facturas={datos.facturas_meta} />
                  </Card>
                )}
                <Notas datos={datos} enMxn={enMxn} />
              </div>
            </div>
          </div>
        )}
      </OsApp>
    </div>
  );
}

function Totales({ datos, dinero }: { datos: ResumenGasto; dinero: (v: number | null) => string }) {
  const tile = 'orion-card min-w-0 rounded-[20px] border border-border bg-card p-4 sm:p-5';
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
      <div className={`${tile} col-span-2 lg:col-span-1`}>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="orion-tone tone-purple flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-11 sm:w-11" aria-hidden>
            <CircleDollarSign size={18} />
          </span>
          <span className="text-sm font-semibold text-foreground">Total</span>
        </div>
        <div className="mt-4 text-[26px] font-semibold leading-none tracking-tight tabular-nums text-foreground sm:text-[30px]">
          {dinero(datos.total_usd)}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {datos.orgs.length} {datos.orgs.length === 1 ? 'organización' : 'organizaciones'}
        </div>
      </div>
      {PROVEEDORES.map((p, i) => (
        <div key={p.key} className={tile}>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className={`orion-tone tone-${p.tone} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-11 sm:w-11`} aria-hidden>
              {p.icon}
            </span>
            <span className="min-w-0 truncate text-sm font-semibold text-foreground">{p.label}</span>
          </div>
          <div className="mt-4 text-[22px] font-semibold leading-none tracking-tight tabular-nums text-foreground sm:text-[26px]">
            {dinero(datos.por_proveedor[p.key] ?? 0)}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span aria-hidden className="h-2 w-2 rounded-sm" style={{ background: COLOR(i) }} /> en la gráfica
          </div>
        </div>
      ))}
    </div>
  );
}

function Leyenda() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Leyenda">
      {PROVEEDORES.map((p, i) => (
        <li key={p.key} className="flex items-center gap-1.5">
          <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: COLOR(i) }} />
          {p.label}
        </li>
      ))}
    </ul>
  );
}

/** Path de un rectángulo con SOLO las esquinas de arriba redondeadas (extremo de dato). */
function topeRedondo(x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

function GraficaDiaria({ datos, dinero }: { datos: ResumenGasto; dinero: (v: number | null) => string }) {
  const dias = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    const [d0, h0] = [new Date(datos.desde), new Date(datos.hasta)];
    for (let d = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()); d < h0; d.setDate(d.getDate() + 1)) {
      m.set(iso(d), {});
    }
    for (const r of datos.por_dia) {
      const fila = m.get(r.dia) ?? {};
      fila[r.proveedor] = (fila[r.proveedor] ?? 0) + r.usd;
      m.set(r.dia, fila);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [datos]);
  const [foco, setFoco] = useState<number | null>(null);
  const caja = useRef<HTMLDivElement>(null);
  // El SVG se dibuja al ancho REAL del contenedor: escalarlo proporcional lo
  // dejaba centrado y diminuto en el celular.
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = caja.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const max = Math.max(...dias.map(([, f]) => Object.values(f).reduce((a, b) => a + b, 0)), 0);
  if (max === 0) {
    return (
      <EmptyState icon={<Receipt size={18} />} title="Sin gasto registrado en este periodo">
        El registro de WhatsApp, voz, Twilio y ElevenLabs empezó con esta versión; la IA de texto ya venía de antes.
      </EmptyState>
    );
  }
  const H = W < 500 ? 160 : 200, pad = 4;
  const ancho = W / dias.length;
  const barra = Math.max(2, Math.min(28, ancho - 4));
  const fila = foco !== null ? dias[foco] : null;

  return (
    <div className="flex flex-col gap-3">
      <Leyenda />
      <div ref={caja} className="relative" onMouseLeave={() => setFoco(null)}>
        <svg viewBox={`0 0 ${W} ${H + 18}`} width={W} height={H + 18} className="block max-w-full" role="img" aria-label="Gasto por día, apilado por proveedor">
          <line x1={0} y1={H} x2={W} y2={H} stroke="hsl(var(--border))" strokeWidth={1} />
          {dias.map(([dia, f], i) => {
            const x = i * ancho + (ancho - barra) / 2;
            let y = H;
            const segmentos = PROVEEDORES.map((p, j) => ({ j, v: f[p.key] ?? 0 })).filter((s) => s.v > 0);
            const total = segmentos.reduce((a, s) => a + s.v, 0);
            return (
              <g key={dia}>
                {segmentos.map((s, k) => {
                  const h = Math.max(1, ((H - pad) * s.v) / max);
                  y -= h;
                  const tope = k === segmentos.length - 1;
                  // 2 px de superficie entre segmentos (borde con el color de la tarjeta).
                  return tope ? (
                    <path key={s.j} d={topeRedondo(x, y, barra, h, 4)} fill={COLOR(s.j)} stroke="hsl(var(--card))" strokeWidth={2} />
                  ) : (
                    <rect key={s.j} x={x} y={y} width={barra} height={h} fill={COLOR(s.j)} stroke="hsl(var(--card))" strokeWidth={2} />
                  );
                })}
                {/* zona de toque más grande que la barra */}
                <rect
                  x={i * ancho} y={0} width={ancho} height={H} fill="transparent" tabIndex={0}
                  aria-label={`${dia}: ${dinero(total)}`}
                  onMouseEnter={() => setFoco(i)} onFocus={() => setFoco(i)} onBlur={() => setFoco(null)}
                  className="outline-none focus-visible:stroke-[hsl(var(--primary))]"
                />
                {(dias.length <= Math.floor(W / 48) || i % Math.ceil(dias.length / Math.max(2, Math.floor(W / 64))) === 0) && (
                  <text x={i * ancho + ancho / 2} y={H + 14} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                    {dia.slice(8)}/{dia.slice(5, 7)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {fila && foco !== null && (
          <div
            role="status"
            className="pointer-events-none absolute top-0 z-10 min-w-[160px] rounded-xl border border-border bg-popover p-3 text-xs text-popover-foreground shadow-lg"
            style={{ left: `min(calc(${((foco + 0.5) / dias.length) * 100}% + 8px), calc(100% - 170px))` }}
          >
            <p className="mb-1 font-semibold">{fila[0]}</p>
            {PROVEEDORES.map((p, i) => (
              <p key={p.key} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5">
                  <span aria-hidden className="h-2 w-2 rounded-sm" style={{ background: COLOR(i) }} />{p.label}
                </span>
                <span className="tabular-nums">{dinero(fila[1][p.key] ?? 0)}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BarraParticion({ org }: { org: GastoOrg }) {
  const porProv = PROVEEDORES.map((p, i) => ({
    i, label: p.label,
    usd: Object.values(org.categorias).filter((c) => c.proveedor === p.key).reduce((a, c) => a + c.usd, 0),
  }));
  const total = porProv.reduce((a, p) => a + p.usd, 0);
  if (total <= 0) return <div className="h-2 rounded-full bg-muted" aria-hidden />;
  return (
    <div className="flex h-2 w-full gap-[2px] overflow-hidden rounded-full" aria-hidden>
      {porProv.filter((p) => p.usd > 0).map((p) => (
        <span key={p.i} style={{ width: `${(p.usd / total) * 100}%`, background: COLOR(p.i) }} />
      ))}
    </div>
  );
}

function ListaOrgs({ orgs, total, dinero }: { orgs: GastoOrg[]; total: number; dinero: (v: number | null) => string }) {
  const [abierta, setAbierta] = useState<string | null>(null);
  if (!orgs.length) return <EmptyState title="Sin gasto en este periodo" />;
  return (
    <ul className="flex flex-col divide-y divide-border">
      {orgs.map((o) => {
        const abierto = abierta === o.org_id;
        const pct = total > 0 ? Math.round((o.total_usd / total) * 100) : 0;
        return (
          <li key={o.org_id || '_sin'} className="py-3">
            <button
              type="button"
              aria-expanded={abierto}
              onClick={() => setAbierta(abierto ? null : o.org_id)}
              className="flex w-full items-center gap-3 rounded-lg text-left hover:bg-muted/40"
            >
              {abierto ? <ChevronDown size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                       : <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden />}
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">{o.nombre}</span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{dinero(o.total_usd)}</span>
                </span>
                <span className="mt-1.5 flex items-center gap-2">
                  <span className="min-w-0 flex-1"><BarraParticion org={o} /></span>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{pct}%</span>
                </span>
              </span>
            </button>
            {abierto && (
              <ul className="mt-3 flex flex-col pl-7">
                {Object.entries(o.categorias).sort(([, a], [, b]) => b.usd - a.usd).map(([k, c]) => (
                  <li key={k} className="flex items-start justify-between gap-3 border-t border-border py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block text-foreground">{c.etiqueta}</span>
                      <span className="block text-xs tabular-nums text-muted-foreground">
                        {c.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 2 })} {c.unidad}
                        {c.sin_costo > 0 && <> · {c.sin_costo} sin tarifa</>}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-foreground">{dinero(c.usd)}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function FacturasMeta({ facturas }: { facturas: FacturaMeta[] }) {
  if (!facturas.length) return <EmptyState title="Sin cuentas de WhatsApp conectadas" />;
  return (
    <ul className="flex flex-col divide-y divide-border">
      {facturas.map((f) => (
        <li key={f.waba_id} className="flex flex-col gap-1.5 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{f.nombre || f.waba_id}</p>
            <p className="truncate text-xs text-muted-foreground">{f.orgs.join(' · ')}</p>
          </div>
          {f.error ? (
            <span className="text-xs text-destructive">No se pudo leer: {f.error}</span>
          ) : !f.con_forma_de_pago ? (
            <span className="self-start rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
              Sin forma de pago: las plantillas no salen
            </span>
          ) : (
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {(f.total ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {f.moneda}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Notas({ datos, enMxn }: { datos: ResumenGasto; enMxn: boolean }) {
  const t = datos.tarifas;
  return (
    <div className="rounded-xl border border-dashed border-border px-4 py-3 text-xs leading-relaxed text-muted-foreground">
      <p>
        <strong className="text-foreground">Qué es real y qué es estimado.</strong> Twilio y las llamadas de ElevenLabs usan el precio que
        reporta el proveedor. WhatsApp se estima por mensaje con la tarifa de México vigente desde {t.whatsapp_vigente_desde}; la factura real
        de Meta va arriba, por cuenta. La IA se estima por token.
        {t.elevenlabs_usd_por_credito == null && ' La voz de ElevenLabs no tiene precio del plan configurado: se cuentan los créditos, sin costo.'}
      </p>
      {!datos.tipo_de_cambio_mxn && <p className="mt-1">Montos en dólares: falta configurar el tipo de cambio para verlos en pesos.</p>}
      {datos.tipo_de_cambio_mxn && enMxn && <p className="mt-1">En pesos con tipo de cambio {datos.tipo_de_cambio_mxn} MXN por dólar.</p>}
      <p className="mt-1">Municipio de SLP no aparece: vive en su propio servidor.</p>
    </div>
  );
}
